import { FC, useRef, useState } from "react";
import styled from "styled-components";
import { useOnClickOutside } from "src/hooks/useOnClickOutside";
import { Feedback } from "src/types/feedback";

const PopoverContainer = styled.div`
	position: relative;
	display: inline-block;
`;

const PopoverTrigger = styled.div`
	cursor: pointer;
	padding: 4px;
	border-radius: 4px;

	&:hover {
		background: ${(props) => props.theme.colors.gray_02};
	}
`;

const PopoverContent = styled.div<{ isOpen: boolean }>`
	display: ${(props) => (props.isOpen ? "block" : "none")};
	position: absolute;
	right: 0;
	top: 100%;
	margin-top: 4px;
	background: white;
	border-radius: 8px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
	z-index: 1000;
	min-width: 120px;
	overflow: hidden;
`;

const MenuItem = styled.div`
	padding: 8px 16px;
	display: flex;
	align-items: center;
	gap: 8px;
	cursor: pointer;
	transition: background-color 0.2s;
	color: ${(props) => props.theme.colors.text_01};

	&:hover {
		background: ${(props) => props.theme.colors.gray_02};
	}

	svg {
		width: 16px;
		height: 16px;
	}
`;

interface Props {
	feedback: Feedback;
	onPin: () => void;
	onDelete: () => void;
	onView: () => void;
	isPinned: boolean;
}

export const FeedbackActionMenu: FC<Props> = ({
	feedback,
	onPin,
	onDelete,
	onView,
	isPinned,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const popoverRef = useRef<HTMLDivElement>(null);

	useOnClickOutside(popoverRef, () => setIsOpen(false));

	return (
		<PopoverContainer ref={popoverRef}>
			<PopoverTrigger onClick={() => setIsOpen(!isOpen)}>
				<svg
					width='24'
					height='24'
					viewBox='0 0 24 24'
					fill='none'
					xmlns='http://www.w3.org/2000/svg'
				>
					<path
						d='M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z'
						stroke='currentColor'
						strokeWidth='2'
						strokeLinecap='round'
						strokeLinejoin='round'
					/>
					<path
						d='M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z'
						stroke='currentColor'
						strokeWidth='2'
						strokeLinecap='round'
						strokeLinejoin='round'
					/>
					<path
						d='M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z'
						stroke='currentColor'
						strokeWidth='2'
						strokeLinecap='round'
						strokeLinejoin='round'
					/>
				</svg>
			</PopoverTrigger>
			<PopoverContent isOpen={isOpen}>
				<MenuItem>
					<button
						onClick={(e) => {
							e.stopPropagation();
							onView();
							setIsOpen(false);
						}}
						className='whitespace-nowrap'
					>
						View
					</button>
				</MenuItem>
				<MenuItem>
					<button
						onClick={(e) => {
							e.stopPropagation();
							onPin();
							setIsOpen(false);
						}}
						className='whitespace-nowrap'
					>
						{isPinned ? "Unpin Feedback" : "Pin Feedback"}
					</button>
				</MenuItem>
				<MenuItem>
					<button
						onClick={(e) => {
							e.stopPropagation();
							onDelete();
							setIsOpen(false);
						}}
					>
						Delete
					</button>
				</MenuItem>
			</PopoverContent>
		</PopoverContainer>
	);
};
